import React, { useState, useEffect } from 'react';
import api from '../../api/auth';

const AdminNotes = () => {
  const [activeTab, setActiveTab] = useState('pending');
  const [pendingGrades, setPendingGrades] = useState([]);
  const [allGrades, setAllGrades] = useState([]);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);

  const [filterClass, setFilterClass] = useState('');
  const [filterSubject, setFilterSubject] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [pendingRes, allRes, classesRes, subjectsRes] = await Promise.all([
        api.get('/admin/grades/pending', { headers }),
        api.get('/admin/grades', { headers }),
        api.get('/classes', { headers }),
        api.get('/subjects', { headers }),
      ]);

      setPendingGrades(Array.isArray(pendingRes.data) ? pendingRes.data : []);
      setAllGrades(Array.isArray(allRes.data) ? allRes.data : []);
      setClasses(Array.isArray(classesRes.data) ? classesRes.data : []);
      setSubjects(Array.isArray(subjectsRes.data) ? subjectsRes.data : []);
    } catch (err) {
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };

  const refreshHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filterClass) params.append('class_id', filterClass);
      if (filterSubject) params.append('subject_id', filterSubject);
      if (filterStatus) params.append('status', filterStatus);

      const res = await api.get(`/admin/grades?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllGrades(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Erreur:', err);
    }
  };

  useEffect(() => {
    if (activeTab === 'history') {
      refreshHistory();
    }
  }, [filterClass, filterSubject, filterStatus, activeTab]);

  const handleValidate = async (id) => {
    if (!window.confirm('Valider cette note ?')) return;
    try {
      setProcessing(id);
      const token = localStorage.getItem('token');
      await api.post(`/admin/grades/${id}/validate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('✅ Note validée');
      fetchData();
    } catch (err) {
      alert('❌ Erreur : ' + (err.response?.data?.error || err.message));
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (id) => {
    if (!window.confirm('Rejeter et SUPPRIMER cette note ?')) return;
    try {
      setProcessing(id);
      const token = localStorage.getItem('token');
      await api.post(`/admin/grades/${id}/reject`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('🗑️ Note rejetée');
      fetchData();
    } catch (err) {
      alert('❌ Erreur : ' + (err.response?.data?.error || err.message));
    } finally {
      setProcessing(null);
    }
  };

  const handleValidateAll = async () => {
    if (pendingGrades.length === 0) return;
    if (!window.confirm(`Valider TOUTES les ${pendingGrades.length} notes en attente ?`)) return;
    try {
      const token = localStorage.getItem('token');
      const res = await api.post('/admin/grades/validate-all-pending', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('✅ ' + res.data.message);
      fetchData();
    } catch (err) {
      alert('❌ Erreur : ' + (err.response?.data?.error || err.message));
    }
  };

  const getScoreColor = (score, maxScore) => {
    const pct = (score / maxScore) * 100;
    if (pct >= 80) return 'text-green-600';
    if (pct >= 60) return 'text-blue-600';
    if (pct >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  const totalGrades = allGrades.length;
  const validatedCount = allGrades.filter((g) => g.is_published).length;
  const pendingCount = allGrades.filter((g) => !g.is_published).length;

  if (loading) return <div className="p-6">Chargement...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-[#2C3E50]">📝 Gestion des notes</h1>
          <p className="text-gray-500">Validez et consultez toutes les notes</p>
        </div>
        {activeTab === 'pending' && pendingGrades.length > 0 && (
          <button
            onClick={handleValidateAll}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
          >
            ✅ Tout valider ({pendingGrades.length})
          </button>
        )}
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl shadow-md p-4 text-center border-l-4 border-blue-500">
          <p className="text-sm text-gray-500">Total notes</p>
          <p className="text-2xl font-bold text-blue-600">{totalGrades}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-4 text-center border-l-4 border-green-500">
          <p className="text-sm text-gray-500">Validées</p>
          <p className="text-2xl font-bold text-green-600">{validatedCount}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-4 text-center border-l-4 border-yellow-500">
          <p className="text-sm text-gray-500">En attente</p>
          <p className="text-2xl font-bold text-yellow-600">{pendingCount}</p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('pending')}
            className={`flex-1 py-3 px-4 text-sm font-medium transition ${
              activeTab === 'pending'
                ? 'bg-orange-50 text-[#E67E22] border-b-2 border-[#E67E22]'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            ⏳ En attente ({pendingGrades.length})
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`flex-1 py-3 px-4 text-sm font-medium transition ${
              activeTab === 'history'
                ? 'bg-orange-50 text-[#E67E22] border-b-2 border-[#E67E22]'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            📚 Historique ({allGrades.length})
          </button>
        </div>

        {activeTab === 'pending' && (
          <div>
            {pendingGrades.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <p className="text-lg mb-2">🎉 Aucune note en attente</p>
                <p className="text-sm">Toutes les notes ont été validées.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Élève</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Classe</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Matière</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Évaluation</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Note</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Prof</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingGrades.map((g) => (
                      <tr key={g.id} className="border-t hover:bg-gray-50">
                        <td className="py-3 px-4 font-medium">{g.student_name}</td>
                        <td className="py-3 px-4 text-sm">{g.class_name}</td>
                        <td className="py-3 px-4 text-sm">{g.subject}</td>
                        <td className="py-3 px-4 text-sm">{g.evaluation_title}</td>
                        <td className={`py-3 px-4 font-bold ${getScoreColor(g.score, g.max_score)}`}>
                          {g.score}/{g.max_score}
                        </td>
                        <td className="py-3 px-4 text-sm">{g.teacher_name}</td>
                        <td className="py-3 px-4">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleValidate(g.id)}
                              disabled={processing === g.id}
                              className="px-3 py-1 bg-green-500 text-white rounded-lg text-xs hover:bg-green-600 disabled:opacity-50"
                            >
                              ✅
                            </button>
                            <button
                              onClick={() => handleReject(g.id)}
                              disabled={processing === g.id}
                              className="px-3 py-1 bg-red-500 text-white rounded-lg text-xs hover:bg-red-600 disabled:opacity-50"
                            >
                              ❌
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'history' && (
          <div>
            <div className="p-4 bg-gray-50 border-b border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <select
                  value={filterClass}
                  onChange={(e) => setFilterClass(e.target.value)}
                  className="px-3 py-2 border rounded-lg text-sm"
                >
                  <option value="">Toutes les classes</option>
                  {classes.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>

                <select
                  value={filterSubject}
                  onChange={(e) => setFilterSubject(e.target.value)}
                  className="px-3 py-2 border rounded-lg text-sm"
                >
                  <option value="">Toutes les matières</option>
                  {subjects.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>

                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="px-3 py-2 border rounded-lg text-sm"
                >
                  <option value="">Tous les statuts</option>
                  <option value="validated">✅ Validées</option>
                  <option value="pending">⏳ En attente</option>
                </select>

                <button
                  onClick={() => {
                    setFilterClass('');
                    setFilterSubject('');
                    setFilterStatus('');
                  }}
                  className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm hover:bg-gray-300"
                >
                  🔄 Réinitialiser
                </button>
              </div>
            </div>

            {allGrades.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <p className="text-lg mb-2">📭 Aucune note trouvée</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Élève</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Classe</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Matière</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Évaluation</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Note</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Prof</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Statut</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allGrades.map((g) => (
                      <tr key={g.id} className="border-t hover:bg-gray-50">
                        <td className="py-3 px-4 font-medium">{g.student_name}</td>
                        <td className="py-3 px-4 text-sm">{g.class_name}</td>
                        <td className="py-3 px-4 text-sm">{g.subject}</td>
                        <td className="py-3 px-4 text-sm">{g.evaluation_title}</td>
                        <td className={`py-3 px-4 font-bold ${getScoreColor(g.score, g.max_score)}`}>
                          {g.score}/{g.max_score}
                        </td>
                        <td className="py-3 px-4 text-sm">{g.teacher_name}</td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            g.is_published
                              ? 'bg-green-100 text-green-700'
                              : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {g.is_published ? '✅ Validée' : '⏳ En attente'}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-xs text-gray-500">{g.created_at}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminNotes;